import { useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import {
  CalendarIcon,
  ArrowRight,
  ArrowLeft,
  Check,
  Building2,
  User,
  CalendarClock,
  UtensilsCrossed,
  Wallet,
  PencilLine,
} from "lucide-react";
import { toast, Toaster } from "sonner";

import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { CONSULT as C_IDS } from "@/constants/testIds";
import { CONSULT } from "@/data/content";
import Seo from "@/components/site/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  brand: "ia",
  name: "",
  email: "",
  phone: "",
  whatsappOK: true,
  event_type: "",
  event_date: undefined,
  dateTBD: false,
  guest_count: "",
  venue: "",
  cuisines: [],
  dietary: [],
  service_styles: [],
  budget_range: "",
  discovery: "",
  message: "",
  company_website: "", // honeypot
};

const STEPS = [
  { id: 1, label: "Brand",       icon: Building2 },
  { id: 2, label: "Contact",     icon: User },
  { id: 3, label: "Event",       icon: CalendarClock },
  { id: 4, label: "Preferences", icon: UtensilsCrossed },
  { id: 5, label: "Budget",      icon: Wallet },
  { id: 6, label: "Notes",       icon: PencilLine },
];

const stepVariants = {
  enter: { opacity: 0, y: 20 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#3B1F1A]/25 focus:border-[#C9A961] outline-none py-3 text-[#3B1F1A] placeholder:text-[#3B1F1A]/40 font-sans text-base focus:ring-0";
const selectTriggerCls =
  "w-full bg-transparent border-0 border-b border-[#3B1F1A]/25 rounded-none px-0 py-3 h-auto shadow-none focus:border-[#C9A961] focus:ring-0 text-base text-[#3B1F1A]";

export default function Consultation() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1); // 1 next, -1 back
  const [form, setForm] = useState({
    ...initial,
    brand: params.get("brand") === "ph" ? "ph" : "ia",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggle = (k, v) =>
    setForm((s) => ({
      ...s,
      [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v],
    }));

  const stepErrors = useMemo(() => {
    const e = {};
    if (step === 2) {
      if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please share your name.";
      if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email.";
      if (!form.phone.trim() || form.phone.trim().length < 6) e.phone = "Enter a valid phone.";
    }
    if (step === 3) {
      if (!form.event_type) e.event_type = "Pick the closest match.";
    }
    return e;
  }, [step, form]);

  const goNext = () => {
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      toast.error("A few fields need attention.");
      return;
    }
    setErrors({});
    setDir(1);
    setStep((s) => Math.min(6, s + 1));
  };
  const goBack = () => {
    setErrors({});
    setDir(-1);
    setStep((s) => Math.max(1, s - 1));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        brand: form.brand,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        whatsapp_ok: form.whatsappOK,
        event_type: form.event_type,
        event_date: form.dateTBD
          ? null
          : form.event_date
          ? format(form.event_date, "yyyy-MM-dd")
          : null,
        date_tbd: form.dateTBD,
        guest_count_range: form.guest_count || null,
        venue: form.venue.trim() || null,
        cuisines: form.cuisines,
        dietary: form.dietary,
        service_styles: form.service_styles,
        budget_range: form.budget_range || null,
        discovery: form.discovery || null,
        message: form.message.trim(),
        company_website: form.company_website,
      };
      const { data } = await axios.post(`${API}/inquiries`, payload);
      setSubmitted(true);
      toast.success("Brief received — we'll be in touch within one working day.", {
        description: `Ref ${data.id?.slice(0, 8)}`,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again in a moment.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF6] pt-32">
        <Seo
          title="Book a Catering Consultation — Indian Alchemy × Platinum Hospitality"
          description="Send us your brief for luxury event catering (weddings, cocktails, high teas) or corporate cafeteria services in Gurugram & Delhi NCR. One of the founders replies personally within one working day."
          path="/book"
          keywords="book catering consultation Gurugram, wedding caterer enquiry Delhi NCR, corporate catering enquiry Gurgaon, Indian Alchemy contact"
        />
        <Toaster position="bottom-right" />
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center" data-testid={C_IDS.successCard}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center justify-center h-16 w-16 rounded-full border border-[#C9A961] text-[#C9A961]"
          >
            <Check size={26} />
          </motion.div>
          <h1 className="mt-10 font-serif font-light text-5xl md:text-6xl leading-[1.05] text-[#3B1F1A]">
            Thank you.<br />
            <em className="italic text-[#C9A961]">We are cooking up a reply.</em>
          </h1>
          <p className="mt-6 text-[#3B1F1A]/70 leading-relaxed">
            Our team replies to every brief personally within one working day. You will hear from Jhanvi or Atul directly.
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <a
              href="/"
              data-testid="consult-success-home"
              className="inline-flex items-center gap-3 h-11 px-6 rounded-full bg-[#C9A961] text-[#3B1F1A] text-[11px] uppercase tracking-[0.28em] hover:bg-[#3B1F1A] hover:text-[#C9A961]"
            >
              Back Home
            </a>
            <a
              href="/portfolio"
              data-testid="consult-success-portfolio"
              className="inline-flex items-center gap-3 h-11 px-6 rounded-full border border-[#3B1F1A]/30 text-[#3B1F1A] text-[11px] uppercase tracking-[0.28em] hover:border-[#C9A961] hover:text-[#C9A961]"
            >
              View Our Work
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FDFBF6] min-h-screen">
      <Seo
        title="Book a Catering Consultation — Indian Alchemy × Platinum Hospitality"
        description="Send us your brief for luxury event catering (weddings, cocktails, high teas) or corporate cafeteria services in Gurugram & Delhi NCR. One of the founders replies personally within one working day."
        path="/book"
        keywords="book catering consultation Gurugram, wedding caterer enquiry Delhi NCR, corporate catering enquiry Gurgaon, Indian Alchemy contact"
      />
      <Toaster position="bottom-right" />

      <section className="pt-32 md:pt-40 pb-8">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
            Book a Consultation · Brief-Builder
          </div>
          <h1 className="mt-6 font-serif font-light text-5xl sm:text-6xl leading-[1.02] text-[#3B1F1A]">
            Tell us about the <em className="italic text-[#C9A961]">room</em>.
          </h1>
        </div>
      </section>

      {/* Progress indicator */}
      <section className="pb-8">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <ol className="grid grid-cols-6 gap-2" data-testid="consult-progress">
            {STEPS.map((s) => {
              const done = step > s.id;
              const active = step === s.id;
              const Icon = s.icon;
              return (
                <li
                  key={s.id}
                  data-testid={`consult-progress-step-${s.id}`}
                  data-active={active ? "true" : "false"}
                  data-done={done ? "true" : "false"}
                  className="flex flex-col items-center gap-2"
                >
                  <div
                    className={`h-1 w-full ${
                      active || done ? "bg-[#C9A961]" : "bg-[#3B1F1A]/15"
                    } transition-colors duration-500`}
                  />
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-medium ${
                        active
                          ? "bg-[#3B1F1A] text-[#C9A961]"
                          : done
                          ? "bg-[#C9A961] text-[#3B1F1A]"
                          : "bg-[#3B1F1A]/10 text-[#3B1F1A]/60"
                      }`}
                    >
                      {done ? <Check size={12} /> : s.id}
                    </span>
                    <span
                      className={`hidden md:inline text-[10px] uppercase tracking-[0.24em] ${
                        active
                          ? "text-[#3B1F1A]"
                          : done
                          ? "text-[#C9A961]"
                          : "text-[#3B1F1A]/40"
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      <section className="pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <form
            data-testid={C_IDS.form}
            onSubmit={(e) => e.preventDefault()}
            noValidate
            className="min-h-[420px]"
          >
            {/* honeypot */}
            <input
              type="text"
              name="company_website"
              tabIndex={-1}
              autoComplete="off"
              data-testid={C_IDS.honeypot}
              value={form.company_website}
              onChange={(e) => set("company_website", e.target.value)}
              className="hidden"
              aria-hidden
            />

            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={step}
                custom={dir}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`consult-step-${step}`}
              >
                {step === 1 && (
                  <div>
                    <StepHead num="01" title="I'm looking for…" caption="Pick the arm of the family you want to work with." />
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <BrandChoice
                        active={form.brand === "ia"}
                        onClick={() => set("brand", "ia")}
                        testId={C_IDS.toggleIA}
                        kicker="Indian Alchemy™"
                        title="Premium Event"
                        body="Weddings, cocktail evenings, high tea, destination events, food festivals."
                        tone="ia"
                      />
                      <BrandChoice
                        active={form.brand === "ph"}
                        onClick={() => set("brand", "ph")}
                        testId={C_IDS.togglePH}
                        kicker="Platinum Hospitality"
                        title="Corporate Catering"
                        body="Daily cafeteria, corporate events, offsites, product launches, annual days."
                        tone="ph"
                      />
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <StepHead num="02" title="Who should we reply to?" caption="One of the founders will read this personally." />
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                      <Field label="Full Name" error={errors.name}>
                        <input data-testid={C_IDS.name} className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                      </Field>
                      <Field label="Email" error={errors.email}>
                        <input data-testid={C_IDS.email} type="email" className={inputCls} placeholder="you@company.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
                      </Field>
                      <Field label="Phone" error={errors.phone}>
                        <input data-testid={C_IDS.phone} type="tel" className={inputCls} placeholder="+91 …" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                        <label className="mt-3 inline-flex items-center gap-2 text-xs cursor-pointer text-[#3B1F1A]/70">
                          <input
                            data-testid={C_IDS.whatsappOK}
                            type="checkbox"
                            checked={form.whatsappOK}
                            onChange={(e) => set("whatsappOK", e.target.checked)}
                            className="accent-[#C9A961]"
                          />
                          Contact me on WhatsApp
                        </label>
                      </Field>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div>
                    <StepHead num="03" title="Tell us about the event." caption="The more you share, the better the first draft will be." />
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                      <Field label="Event / Requirement Type" error={errors.event_type}>
                        <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                          <SelectTrigger data-testid={C_IDS.eventType} className={selectTriggerCls}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {CONSULT.eventTypes.map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Event Date">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              data-testid={C_IDS.date}
                              disabled={form.dateTBD}
                              className={`${inputCls} text-left flex items-center justify-between disabled:opacity-40`}
                            >
                              <span className={form.event_date ? "" : "text-[#3B1F1A]/40"}>
                                {form.event_date ? format(form.event_date, "PPP") : "Pick a date"}
                              </span>
                              <CalendarIcon size={16} className="opacity-60" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={form.event_date} onSelect={(d) => set("event_date", d)} initialFocus disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
                          </PopoverContent>
                        </Popover>
                        <label className="mt-3 inline-flex items-center gap-2 text-xs cursor-pointer text-[#3B1F1A]/70">
                          <input data-testid={C_IDS.dateTBD} type="checkbox" checked={form.dateTBD} onChange={(e) => set("dateTBD", e.target.checked)} className="accent-[#C9A961]" />
                          Date not fixed yet
                        </label>
                      </Field>
                      <Field label="Expected Guest Count">
                        <Select value={form.guest_count} onValueChange={(v) => set("guest_count", v)}>
                          <SelectTrigger data-testid={C_IDS.guestCount} className={selectTriggerCls}>
                            <SelectValue placeholder="Select range" />
                          </SelectTrigger>
                          <SelectContent>
                            {CONSULT.guestRanges.map((r) => (
                              <SelectItem key={r} value={r}>{r}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Venue / City">
                        <input data-testid={C_IDS.venue} className={inputCls} placeholder="e.g. Gurugram, Jaipur" value={form.venue} onChange={(e) => set("venue", e.target.value)} />
                      </Field>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div>
                    <StepHead num="04" title="What should we cook?" caption="Multi-select. We'll design against these." />
                    <div className="mt-10 space-y-8">
                      <Field label="Cuisine Preferences">
                        <ChipGroup options={CONSULT.cuisines} values={form.cuisines} onToggle={(v) => toggle("cuisines", v)} testIdFn={(v) => C_IDS.cuisine(v.toLowerCase().replace(/[^a-z0-9]/g, "-"))} />
                      </Field>
                      <Field label="Dietary Notes">
                        <ChipGroup options={CONSULT.dietary} values={form.dietary} onToggle={(v) => toggle("dietary", v)} testIdFn={(v) => C_IDS.dietary(v.toLowerCase().replace(/[^a-z0-9]/g, "-"))} />
                      </Field>
                      <Field label="Service Style">
                        <ChipGroup options={CONSULT.serviceStyles} values={form.service_styles} onToggle={(v) => toggle("service_styles", v)} testIdFn={(v) => C_IDS.service(v.toLowerCase().replace(/[^a-z0-9]/g, "-"))} />
                      </Field>
                    </div>
                  </div>
                )}

                {step === 5 && (
                  <div>
                    <StepHead num="05" title="Budget & discovery." caption="Both help us send the right first proposal." />
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                      <Field label="Budget Range">
                        <Select value={form.budget_range} onValueChange={(v) => set("budget_range", v)}>
                          <SelectTrigger data-testid={C_IDS.budget} className={selectTriggerCls}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {CONSULT.budgets.map((b) => (
                              <SelectItem key={b} value={b}>{b}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="How did you hear about us?">
                        <Select value={form.discovery} onValueChange={(v) => set("discovery", v)}>
                          <SelectTrigger data-testid={C_IDS.discovery} className={selectTriggerCls}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {CONSULT.discovery.map((d) => (
                              <SelectItem key={d} value={d}>{d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                  </div>
                )}

                {step === 6 && (
                  <div>
                    <StepHead num="06" title="Anything else we should know?" caption="Optional. Then hit send." />
                    <div className="mt-10">
                      <Field label="Additional Notes">
                        <textarea
                          data-testid={C_IDS.notes}
                          rows={5}
                          className={`${inputCls} resize-none`}
                          placeholder="The room, the guest list, cuisines you love, anything we shouldn't miss."
                          value={form.message}
                          onChange={(e) => set("message", e.target.value)}
                        />
                      </Field>
                      <div className="mt-8 p-6 border border-[#C9A961]/40 bg-[#F5C9B0]/20 rounded-sm">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Your brief</div>
                        <ul className="text-sm text-[#3B1F1A]/85 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                          <li><span className="text-[#3B1F1A]/50">Brand · </span>{form.brand === "ph" ? "Platinum Hospitality" : "Indian Alchemy"}</li>
                          <li><span className="text-[#3B1F1A]/50">Event · </span>{form.event_type || "—"}</li>
                          <li><span className="text-[#3B1F1A]/50">Date · </span>{form.dateTBD ? "TBD" : form.event_date ? format(form.event_date, "PPP") : "—"}</li>
                          <li><span className="text-[#3B1F1A]/50">Guests · </span>{form.guest_count || "—"}</li>
                          <li><span className="text-[#3B1F1A]/50">Venue · </span>{form.venue || "—"}</li>
                          <li><span className="text-[#3B1F1A]/50">Budget · </span>{form.budget_range || "—"}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </form>

          {/* Nav row */}
          <div className="mt-14 pt-6 border-t border-[#3B1F1A]/10 flex items-center justify-between">
            <button
              type="button"
              onClick={goBack}
              disabled={step === 1}
              data-testid="consult-back-button"
              className="inline-flex items-center gap-2 h-11 pl-2 pr-5 text-xs uppercase tracking-[0.28em] text-[#3B1F1A]/70 disabled:opacity-30 hover:text-[#C9A961]"
            >
              <ArrowLeft size={14} /> Back
            </button>

            <div className="hidden sm:block text-[11px] uppercase tracking-[0.28em] text-[#3B1F1A]/50 whitespace-nowrap">
              Step {step} <span className="text-[#C9A961]">of</span> {STEPS.length}
            </div>

            {step < 6 ? (
              <button
                type="button"
                onClick={goNext}
                data-testid="consult-next-button"
                className="group inline-flex items-center gap-3 h-12 pl-6 pr-5 rounded-full bg-[#3B1F1A] text-[#C9A961] text-xs uppercase tracking-[0.28em] hover:bg-[#C9A961] hover:text-[#3B1F1A] transition-colors whitespace-nowrap"
              >
                Continue
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#C9A961] text-[#3B1F1A] group-hover:rotate-45 transition-transform duration-500">
                  <ArrowRight size={16} />
                </span>
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid={C_IDS.submit}
                className="group inline-flex items-center gap-3 h-12 pl-6 pr-5 rounded-full bg-[#C9A961] text-[#3B1F1A] text-xs uppercase tracking-[0.28em] hover:bg-[#3B1F1A] hover:text-[#C9A961] transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {submitting ? "Sending…" : "Send My Brief"}
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#3B1F1A] text-[#C9A961] group-hover:rotate-45 transition-transform duration-500">
                  <ArrowRight size={16} />
                </span>
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function StepHead({ num, title, caption }) {
  return (
    <div>
      <div className="font-mono text-[11px] tracking-widest text-[#C9A961]">Step {num}</div>
      <h2 className="mt-3 font-serif font-light text-4xl md:text-5xl leading-[1.05] text-[#3B1F1A]">
        {title}
      </h2>
      {caption && <p className="mt-3 text-sm text-[#3B1F1A]/70">{caption}</p>}
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.3em] mb-1 text-[#3B1F1A]/60">
        {label}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-[#D42B2B]">{error}</span>}
    </label>
  );
}

function ChipGroup({ options, values, onToggle, testIdFn }) {
  return (
    <div className="flex flex-wrap gap-2 pt-2">
      {options.map((o) => {
        const on = values.includes(o);
        return (
          <button
            key={o}
            type="button"
            data-testid={testIdFn ? testIdFn(o) : undefined}
            onClick={() => onToggle(o)}
            className={`text-[11px] uppercase tracking-[0.22em] px-4 h-9 rounded-full border transition-colors ${
              on
                ? "bg-[#C9A961] text-[#3B1F1A] border-[#C9A961]"
                : "border-[#3B1F1A]/20 text-[#3B1F1A] hover:border-[#C9A961] hover:text-[#C9A961]"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function BrandChoice({ active, onClick, testId, kicker, title, body, tone }) {
  const activeClasses =
    tone === "ph"
      ? "border-[#D42B2B] bg-[#D42B2B]/6 shadow-[0_0_40px_-8px_rgba(212,43,43,0.45)]"
      : "border-[#C9A961] bg-[#C9A961]/8 shadow-[0_0_40px_-8px_rgba(201,169,97,0.55)]";
  const kickerColor = tone === "ph" ? "text-[#D42B2B]" : "text-[#C9A961]";
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left p-8 md:p-10 border transition-[border-color,background-color,box-shadow] duration-500 ${
        active ? activeClasses : "border-[#3B1F1A]/15 hover:border-[#3B1F1A]/40"
      }`}
    >
      <div className={`text-[11px] uppercase tracking-[0.28em] ${kickerColor}`}>
        {kicker}
      </div>
      <div className="mt-4 font-serif text-3xl md:text-4xl text-[#3B1F1A]">
        {title}
      </div>
      <p className="mt-4 text-sm text-[#3B1F1A]/70 leading-relaxed">{body}</p>
      <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#3B1F1A]">
        {active ? (
          <>
            <Check size={12} className={tone === "ph" ? "text-[#D42B2B]" : "text-[#C9A961]"} />
            Selected
          </>
        ) : (
          "Choose"
        )}
      </div>
    </button>
  );
}
